import React, { useState, useRef, ReactNode, useEffect } from 'react';
import { Popover, Checkbox, Chip, TextField, List, ListItem, ListItemButton, ListItemText, ListItemIcon, IconButton, Badge, Box, Button, Stack, Paper, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface OptionType {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
}

interface FilterDropdownProps {
  label: string;
  options: OptionType[];
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  multiSelect?: boolean;
  singleSelect?: boolean;
  placeholder?: string;
  selectedCountBadge?: boolean;
  button?: ReactNode;
  hideSearch?: boolean;
  extraStyle?: React.CSSProperties;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  multiSelect = false,
  singleSelect = false,
  placeholder = '',
  selectedCountBadge = false,
  button,
  hideSearch = false,
  extraStyle = {},
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState('');
  const [usExpanded, setUsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isLocationFilter = label === 'Location';

  const open = Boolean(anchorEl);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setTimeout(() => {
      inputRef.current && inputRef.current.focus();
    }, 100);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  useEffect(() => {
    if (isLocationFilter && search && !usExpanded) {
      const searchLower = search.toLowerCase();
      const matchesState = options.some(opt => 
        opt.isState && opt.label.toLowerCase().includes(searchLower)
      );
      if (matchesState) {
        setUsExpanded(true);
      }
    }
  }, [search, isLocationFilter, options, usExpanded]);

  const filteredOptions = options.filter(opt => {
    const labelLower = opt.label.toLowerCase();
    const searchLower = search.toLowerCase();
    const matchesSearch = labelLower.includes(searchLower);

    if (!matchesSearch) return false;

    if (isLocationFilter) {
        if (opt.isState && !usExpanded) {
            return labelLower.includes(searchLower);
        }
    }
    
    return true;
  });

  const handleSelect = (optionValue: string) => {
    if (multiSelect && Array.isArray(value)) {
      const isUS = optionValue === 'US';
      const isState = optionValue.startsWith('US-');
      let newValue = [...value];

      if (value.includes(optionValue)) {
        newValue = value.filter(v => v !== optionValue);
      } else {
         newValue = [...value, optionValue];
      }
      onChange(newValue);

    } else if (singleSelect) {
      onChange(optionValue);
      handleClose();
    }
  };

  const handleDeleteChip = (chipValue: string) => {
    if (multiSelect && Array.isArray(value)) {
      onChange(value.filter(v => v !== chipValue));
    }
  };

  const selectedOptions = multiSelect && Array.isArray(value)
    ? options.filter(opt => value.includes(opt.value))
    : singleSelect && typeof value === 'string'
      ? options.filter(opt => opt.value === value)
      : [];

  return (
    <Box sx={{ position: 'relative', display: 'inline-block', ...extraStyle }}>
      {button ? (
        <Box onClick={handleOpen} sx={{ cursor: 'pointer', position: 'relative' }}>{button}</Box>
      ) : (
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 999,
            px: 2,
            py: 1,
            minWidth: 120,
            boxShadow: 1,
            textTransform: 'none',
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1303,
            '&:hover': { boxShadow: 3, backgroundColor: 'white' },            
          }}
          endIcon={<ArrowDropDownIcon />}
        >
          <Box sx={{ mr: 1, fontWeight: 500, color: 'grey.800' }}>{label}</Box>
          {selectedCountBadge && selectedOptions.length > 0 && (
            <Badge badgeContent={selectedOptions.length} color="primary" sx={{ mr: 1 }} />
          )}
          <Stack direction="row" spacing={0.5} sx={{ maxWidth: 120, overflowX: 'auto' }}>
            {selectedOptions.map(opt => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onDelete={multiSelect ? () => handleDeleteChip(opt.value) : undefined}
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Stack>
        </Button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { minWidth: anchorEl?.offsetWidth } }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>{label}</Typography>
          {!hideSearch && (
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <TextField
                inputRef={inputRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={placeholder || `Search ${label}`}
                size="small"
                fullWidth
                variant="outlined"
              />
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
          <List dense sx={{ maxHeight: 224, overflowY: 'auto' }}>
            {filteredOptions.length === 0 && (
              <ListItem>
                <ListItemText primary="No options found" />
              </ListItem>
            )}
            {filteredOptions.map(opt => {
              const isSelected = multiSelect ? Array.isArray(value) && value.includes(opt.value) : value === opt.value;
              const isUSOption = isLocationFilter && opt.value === 'US';
              const isStateOption = isLocationFilter && opt.isState;

              return (
                <ListItemButton
                  key={opt.value}
                  dense
                  onClick={() => handleSelect(opt.value)}
                  selected={isSelected}
                  sx={{
                    height: '30px !important',
                    pl: isStateOption ? 4 : (multiSelect ? 0 : 2),
                    pr: isUSOption ? 1 : (multiSelect ? 2 : 2),
                  }}
                >
                  {multiSelect && (
                    <ListItemIcon sx={{ minWidth: 0, mr: 1.5 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={opt.label} />
                  {isUSOption && (
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUsExpanded(!usExpanded);
                      }}
                      sx={{ 
                        ml: 1, 
                        visibility: options.some(o => o.isState) ? 'visible' : 'hidden' 
                      }}
                    >
                      {usExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                  )}
                </ListItemButton>
              );
            })}
          </List>          
        </Box>
      </Popover>
    </Box>
  );
};

export default FilterDropdown; 